import { quoteFirstCleanPrice } from '@shared/first-clean-price.ts';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { readerFor } from '../../i18n/localized.ts';
import { templateApi } from '../../api/endpoints.ts';
import { temporaryId } from '../../hooks/quote-merge.ts';
import { NumberInput } from '../number-input.tsx';
import { Select, TextInput } from '../ui.tsx';
import { TemplatePicker } from '../template-picker.tsx';
import { TASK_CATEGORIES } from '@shared/constants/template-taxonomy.ts';
import { formatMinutes } from '../../i18n/format.ts';
import { taskMinutes } from '@shared/calculations/pricing.ts';
import {
  MAX_MEASURED_SQUARE_FEET,
  MAX_PRODUCTION_RATE_PER_HOUR,
  MEASUREMENT_BASES,
  MIN_PRODUCTION_RATE_PER_HOUR,
  SERVICE_FREQUENCIES,
  type MeasurementBasis,
  type ServiceFrequency,
} from '@shared/constants/index.ts';
import type { Quote, QuoteRoom, QuoteRoomService, ServiceTemplate } from '@shared/types/index.ts';

/**
 * The tasks on one room, as a checklist.
 *
 * The row is a tick, a name, and the minutes. How a commercial task is
 * measured, and any note, stay under More.
 */
export function RoomTasks({
  quote,
  room,
  readOnly,
  segments,
  onChange,
}: {
  quote: Quote;
  room: QuoteRoom;
  readOnly: boolean;
  /**
   * Both segments when a commercial quote has units on. The room picker uses
   * the same list, so the tasks for an apartment room stay available.
   */
  segments?: readonly string[] | null;
  onChange: (room: QuoteRoom) => void;
}) {
  const { t } = useTranslation();
  const localized = readerFor(quote.language);
  const [customName, setCustomName] = useState('');
  const commercial = quote.segment === 'commercial';
  // A task can be the first clean's alone while the quote prices one.
  const firstCleanOn = quote.billingBasis === 'per_visit' && quoteFirstCleanPrice(quote) !== null;
  const library = useQuery({
    queryKey: ['service-templates'],
    queryFn: () => templateApi.services(),
    enabled: !readOnly,
  });

  function write(next: QuoteRoom) {
    onChange(next);
  }

  function patchService(serviceId: string, patch: Partial<QuoteRoomService>) {
    write({
      ...room,
      services: room.services.map((service) => (service.id === serviceId ? { ...service, ...patch } : service)),
    });
  }

  function addCustom() {
    const name = customName.trim();
    if (!name) return;
    write({ ...room, services: [...room.services, blankTask(name, room.services.length)] });
    setCustomName('');
  }

  function addFromLibrary(template: ServiceTemplate) {
    write({
      ...room,
      services: [
        ...room.services,
        {
          id: temporaryId(),
          serviceTemplateId: template.id,
          name: template.name,
          description: template.customerDescription,
          nameTranslations: template.nameTranslations,
          descriptionTranslations: template.customerDescriptionTranslations,
          estimatedMinutes: template.defaultMinutes,
          isCustom: false,
          isSelected: true,
          showOnCustomerQuote: template.showOnCustomerQuote,
          isTaxable: template.isTaxable,
          frequency: 'every_visit',
          measurementBasis: template.measurementBasis,
          measuredQuantity: null,
          productionRatePerHour: template.measurementBasis === 'time' ? null : template.productionRatePerHour,
          customerNotes: null,
          internalNotes: null,
          displayOrder: room.services.length,
        },
      ],
    });
  }

  const alreadyOnRoom = new Set(room.services.map((service) => service.serviceTemplateId).filter(Boolean));

  return (
    <div className="mt-3 border-t border-[color:var(--color-line)] pt-3">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-3">
        <h3 className="text-sm font-semibold">{t('quoteWork.tasks')}</h3>
        {commercial && room.services.length > 0 ? (
          <label className="block min-w-0 text-sm">
            <span className="field-label">{t('quoteWork.bulkFrequency')}</span>
            <select
              className="field-control min-h-11"
              disabled={readOnly}
              value=""
              onChange={(event) => {
                const frequency = event.target.value as ServiceFrequency;
                if (!frequency) return;
                write({
                  ...room,
                  services: room.services.map((service) => ({ ...service, frequency })),
                });
              }}
            >
              <option value="">{t('quoteWork.bulkFrequencyPlaceholder')}</option>
              {SERVICE_FREQUENCIES.map((frequency) => (
                <option key={frequency} value={frequency}>
                  {t(`servicesStep.frequency.${frequency}`)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {room.services.length === 0 ? (
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('quoteWork.noTasks')}</p>
      ) : (
        <ul className="mb-3 divide-y divide-[color:var(--color-line)]">
          {room.services.map((service, index) => {
            const name = localized.text(service.name, service.nameTranslations);
            function move(delta: number) {
              const target = index + delta;
              if (target < 0 || target >= room.services.length) return;
              const next = [...room.services];
              const [moved] = next.splice(index, 1);
              if (!moved) return;
              next.splice(target, 0, moved);
              write({
                ...room,
                services: next.map((candidate, order) => ({ ...candidate, displayOrder: order })),
              });
            }
            return (
              <li key={service.id} className="py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex min-h-11 min-w-0 basis-full cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 sm:flex-1 sm:basis-56">
                    <input
                      type="checkbox"
                      className="h-6 w-6 shrink-0"
                      checked={service.isSelected}
                      disabled={readOnly}
                      aria-label={name}
                      onChange={(event) => patchService(service.id, { isSelected: event.target.checked })}
                    />
                    <span className="min-w-0 flex-1 basis-48 font-medium [overflow-wrap:anywhere]">{name}</span>
                  </label>
                  <div className="flex min-w-0 max-w-full flex-wrap items-end gap-2">
                    {service.measurementBasis === 'time' ? (
                      <div className="w-28 min-w-0 max-w-full shrink-0">
                        <NumberInput
                          label={t('servicesStep.minutesLabel')}
                          min={0}
                          max={1000}
                          value={service.estimatedMinutes}
                          disabled={readOnly || !service.isSelected}
                          onValueChange={(minutes) =>
                            patchService(service.id, { estimatedMinutes: Math.max(0, minutes ?? 0) })
                          }
                        />
                      </div>
                    ) : (
                      <span className="shrink-0 text-sm text-[color:var(--color-muted)]">
                        {t(`servicesStep.measuredBy.${service.measurementBasis}`)}
                      </span>
                    )}
                    {!readOnly ? (
                      <>
                        <button
                          type="button"
                          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-[color:var(--color-muted)] hover:text-[color:var(--color-ink)] disabled:opacity-40"
                          aria-label={t('quoteWork.moveTaskUp', { task: name })}
                          disabled={index === 0}
                          onClick={() => move(-1)}
                        >
                          <span aria-hidden="true">↑</span>
                        </button>
                        <button
                          type="button"
                          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-[color:var(--color-muted)] hover:text-[color:var(--color-ink)] disabled:opacity-40"
                          aria-label={t('quoteWork.moveTaskDown', { task: name })}
                          disabled={index === room.services.length - 1}
                          onClick={() => move(1)}
                        >
                          <span aria-hidden="true">↓</span>
                        </button>
                        <button
                          type="button"
                          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-lg text-[color:var(--color-muted)] hover:text-[color:var(--color-danger)]"
                          aria-label={t('servicesStep.removeTask', { task: name })}
                          onClick={() =>
                            write({
                              ...room,
                              services: room.services
                                .filter((candidate) => candidate.id !== service.id)
                                .map((candidate, order) => ({ ...candidate, displayOrder: order })),
                            })
                          }
                        >
                          <span aria-hidden="true">×</span>
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
                {service.isSelected && firstCleanOn ? (
                  <label className="mt-1 flex min-h-11 cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-5 w-5 shrink-0"
                      checked={service.firstVisitOnly === true}
                      disabled={readOnly}
                      aria-label={t('firstClean.firstVisitOnlyFor', { task: name })}
                      onChange={(event) => patchService(service.id, { firstVisitOnly: event.target.checked })}
                    />
                    <span>{t('firstClean.firstVisitOnly')}</span>
                  </label>
                ) : null}
                {service.isSelected ? (
                  <details className="mt-1">
                    <summary className="min-h-11 cursor-pointer text-sm font-semibold">
                      {commercial ? t('quoteBuilder.advanced') : t('quoteWork.more')}
                    </summary>
                    <TaskDetails
                      quote={quote}
                      room={room}
                      service={service}
                      readOnly={readOnly}
                      commercial={commercial}
                      onPatch={(patch) => patchService(service.id, patch)}
                    />
                  </details>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {!readOnly ? (
        <>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-0 flex-1">
              <TextInput
                label={t('quoteWork.addTask')}
                placeholder={t('servicesStep.oneOffPlaceholder')}
                value={customName}
                onChange={(event) => setCustomName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addCustom();
                  }
                }}
              />
            </div>
            <div className="mb-4">
              <button
                type="button"
                className="btn btn-secondary min-h-11"
                disabled={!customName.trim()}
                onClick={addCustom}
              >
                {t('quoteWork.addTask')}
              </button>
            </div>
          </div>
          {(library.data?.items.length ?? 0) > 0 ? (
            <details className="mb-2">
              <summary className="min-h-11 cursor-pointer text-sm font-semibold">
                {t('servicesStep.fromLibraryLabel')}
              </summary>
              <TemplatePicker
                items={(library.data?.items ?? []).filter((service) => !alreadyOnRoom.has(service.id))}
                label={(service) =>
                  t('servicesStep.libraryOption', {
                    name: localized.text(service.name, service.nameTranslations),
                    minutes: service.defaultMinutes,
                  })
                }
                categories={TASK_CATEGORIES.map((code) => ({
                  code,
                  label: t(`libraryTaxonomy.task.${code}`),
                }))}
                onPick={addFromLibrary}
                contextSegment={quote.segment}
                segments={segments}
              />
            </details>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function blankTask(name: string, displayOrder: number): QuoteRoomService {
  return {
    id: temporaryId(),
    serviceTemplateId: null,
    name,
    description: null,
    nameTranslations: null,
    descriptionTranslations: null,
    estimatedMinutes: 15,
    isCustom: true,
    isSelected: true,
    showOnCustomerQuote: true,
    isTaxable: true,
    frequency: 'every_visit',
    measurementBasis: 'time',
    measuredQuantity: null,
    productionRatePerHour: null,
    customerNotes: null,
    internalNotes: null,
    displayOrder,
  };
}

function measurementSwitch(basis: MeasurementBasis, service: QuoteRoomService): Partial<QuoteRoomService> {
  if (basis === 'time') {
    return {
      measurementBasis: basis,
      measuredQuantity: null,
      productionRatePerHour: null,
      estimatedMinutes: service.estimatedMinutes,
    };
  }
  return { measurementBasis: basis };
}

function TaskDetails({
  quote,
  room,
  service,
  readOnly,
  commercial,
  onPatch,
}: {
  quote: Quote;
  room: QuoteRoom;
  service: QuoteRoomService;
  readOnly: boolean;
  commercial: boolean;
  onPatch: (patch: Partial<QuoteRoomService>) => void;
}) {
  const { t } = useTranslation();
  const disabled = readOnly || !service.isSelected;

  return (
    <div className="mt-2 grid gap-x-4 sm:grid-cols-2">
      {commercial ? (
        <Select
          label={t('servicesStep.measuredByLabel')}
          value={service.measurementBasis}
          disabled={disabled}
          onChange={(event) => onPatch(measurementSwitch(event.target.value as MeasurementBasis, service))}
          options={MEASUREMENT_BASES.map((basis) => ({
            value: basis,
            label: t(`servicesStep.measuredBy.${basis}`),
          }))}
        />
      ) : null}
      {commercial ? (
        <Select
          label={t('servicesStep.frequencyLabel')}
          value={service.frequency}
          disabled={disabled}
          onChange={(event) => onPatch({ frequency: event.target.value as ServiceFrequency })}
          options={SERVICE_FREQUENCIES.map((frequency) => ({
            value: frequency,
            label: t(`servicesStep.frequency.${frequency}`),
          }))}
        />
      ) : null}
      {service.measurementBasis !== 'time' ? (
        <>
          <NumberInput
            label={
              service.measurementBasis === 'area' ? t('servicesStep.squareFeetLabel') : t('servicesStep.fixturesLabel')
            }
            hint={
              service.measurementBasis === 'area' && service.measuredQuantity === null
                ? t('servicesStep.squareFeetInheritedHint', { squareFeet: room.measuredSquareFeet ?? 0 })
                : undefined
            }
            min={0}
            max={MAX_MEASURED_SQUARE_FEET}
            value={service.measuredQuantity}
            disabled={disabled}
            onValueChange={(quantity) =>
              onPatch({ measuredQuantity: quantity === null ? null : Math.max(0, Math.trunc(quantity)) })
            }
          />
          <NumberInput
            label={
              service.measurementBasis === 'area' ? t('servicesStep.rateAreaLabel') : t('servicesStep.rateFixtureLabel')
            }
            hint={t('servicesStep.derivedMinutes', {
              time: formatMinutes(taskMinutes(service, { measuredSquareFeet: room.measuredSquareFeet })),
            })}
            min={MIN_PRODUCTION_RATE_PER_HOUR}
            max={MAX_PRODUCTION_RATE_PER_HOUR}
            value={service.productionRatePerHour}
            disabled={disabled}
            onValueChange={(rate) =>
              onPatch({ productionRatePerHour: rate === null ? null : Math.max(1, Math.trunc(rate)) })
            }
          />
        </>
      ) : null}
      <TextInput
        label={t('servicesStep.customerNoteLabel')}
        hint={t('servicesStep.customerNoteHint')}
        value={service.customerNotes ?? ''}
        disabled={disabled}
        onChange={(event) => onPatch({ customerNotes: event.target.value || null })}
      />
      <TextInput
        label={t('servicesStep.crewNoteLabel')}
        hint={t('servicesStep.crewNoteHint')}
        tone="internal"
        value={service.internalNotes ?? ''}
        disabled={disabled}
        onChange={(event) => onPatch({ internalNotes: event.target.value || null })}
      />
    </div>
  );
}
